import { formatNumber, formatCurrency, formatPercent } from '../../lib/utils';

interface KPIS {
  total: number;
  quality: number;
  bots: number;
  convs: number;
  revenue: number;
  cr: number;
  epc: number;
  botRate: number;
  payout?: number;
}

export default function KpiStrip({ kpis }: { kpis: KPIS }) {
  const colorMap = {
    magenta: 'text-neon-magenta',
    cyan: 'text-neon-cyan',
    yellow: 'text-neon-yellow',
    red: 'text-neon-red',
    green: 'text-neon-green',
    tertiary: 'text-text-tertiary',
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
      {/* Clicks */}
      <KpiCell label="Clicks" value={formatNumber(kpis.total)} accent="cyan" />

      {/* Quality */}
      <KpiCell
        label="Quality"
        value={formatNumber(kpis.quality)}
        accent="green"
      />

      {/* Bots */}
      <KpiCell
        label="Bots"
        value={formatNumber(kpis.bots)}
        accent={kpis.botRate > 10 ? 'red' : 'tertiary'}
        hint={kpis.total > 0 ? `${kpis.botRate.toFixed(1)}%` : undefined}
      />

      {/* Convs */}
      <KpiCell
        label="Convs"
        value={formatNumber(kpis.convs)}
        accent="magenta"
      />

      {/* CR */}
      <KpiCell
        label="CR"
        value={formatPercent(kpis.cr, 2)}
        accent="green"
      />

      {/* Revenue */}
      <KpiCell
        label="Revenue"
        value={formatCurrency(kpis.revenue)}
        accent="yellow"
      />

      {/* EPC */}
      <KpiCell
        label="EPC"
        value={formatCurrency(kpis.epc)}
        accent="yellow"
      />

      {/* Payout */}
      <KpiCell
        label="Payout"
        value={formatCurrency(kpis.payout ?? 0)}
        accent="yellow"
      />
    </div>
  );
}

function KpiCell({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent: 'magenta' | 'cyan' | 'yellow' | 'red' | 'green' | 'tertiary';
  hint?: string;
}) {
  const colorMap = {
    magenta: 'text-neon-magenta',
    cyan: 'text-neon-cyan',
    yellow: 'text-neon-yellow',
    red: 'text-neon-red',
    green: 'text-neon-green',
    tertiary: 'text-text-tertiary',
  };

  return (
    <div className="px-2 py-1.5 bg-bg-tertiary border border-border-subtle rounded">
      <div className="text-[9px] font-display uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div
        className={`text-base font-display font-bold numeric ${colorMap[accent]}`}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[9px] text-text-tertiary font-mono">{hint}</div>
      )}
    </div>
  );
}
